import type { Breakpoints } from '@oneaudi/unified-web-common';
import { parseAssetValue } from './parseAssetValue';
import type { AssetTypeVideo } from '../../types';

export type Video = {
  alt?: string;
  aspectRatio: string;
  poster?: string;
  caption?: string;
  audioDescription?: string;
  posterAltText?: string;
  src: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  nativeControls?: boolean;
  hideControls?: boolean;
};

export type VideoPlayerConfig = {
  videos: Partial<Breakpoints<Video>>;
  playsInline: boolean;
  cover?: boolean;
  isFormatable?: boolean;
};

export type VideoPlayerAssetConfig = Omit<AssetTypeVideo, 'portrait' | 'landscape'> & {
  src:
    | {
        assetValue: string;
      }
    | undefined;
  isMobile: boolean;
};

export function getVideoPlayerConfig(video: VideoPlayerAssetConfig): VideoPlayerConfig {
  const relevantVideoString = video.isMobile ? 'portrait' : 'landscape';

  const parsedVideo = parseAssetValue(video.src?.assetValue);
  const parsedCaptions = parseAssetValue(video[`${relevantVideoString}Captions`]?.assetValue);
  const parsedAD = parseAssetValue(video[`${relevantVideoString}AudioDescription`]?.assetValue);
  const videoAltText = video[`${relevantVideoString}AltText`];
  const parsedPoster = parseAssetValue(video[`${relevantVideoString}Poster`]?.assetValue).src;

  // separately provided poster image url overrides the VMS thumbnail image used as poster image
  const posterSrc = parsedPoster || parsedVideo.posterSrc;
  const cover = !video.preserveVideoWidth;

  const shouldShowNativeControls = video.isMobile
    ? (video.hideNativeControlsPortrait ?? false)
    : (video.hideNativeControlsLandscape ?? false);

  return {
    cover,
    playsInline: true,
    videos: {
      xs: {
        alt: videoAltText,
        caption: parsedCaptions.src,
        nativeControls: true,
        hideControls: shouldShowNativeControls,
        audioDescription: parsedAD.src,
        poster: posterSrc,
        aspectRatio: '3/4',
        autoPlay: video.autoPlay,
        loop: video.loop,
        src: parsedVideo.src,
      },
    },
  };
}
